import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isPublicRoute } from '../utils/publicRoutes';

/**
 * Session Context to track active session across all components
 */
const SessionContext = createContext({
    isSessionActive: true,
    invalidateSession: () => { },
});

export const useSession = () => useContext(SessionContext);

/**
 * SessionProvider wraps the app and provides session state to all components
 */
export const SessionProvider = ({ children }) => {
    const [isSessionActive, setIsSessionActive] = useState(true);
    const [sessionId] = useState(() => Date.now().toString());

    // Function to invalidate session
    const invalidateSession = () => {
        setIsSessionActive(false);
        // Clear everything
        localStorage.clear();
        // Force reload to login
        setTimeout(() => {
            window.location.href = '/';
        }, 100);
    };

    // Monitor localStorage for cross-tab logout
    useEffect(() => {
        const checkSession = () => {
            const token = localStorage.getItem('token');
            const currentPath = window.location.pathname;

            // If not on public route and no token, invalidate
            if (!isPublicRoute(currentPath) && !token && isSessionActive) {
                invalidateSession();
            }
        };

        // Check immediately
        checkSession();

        // Check every second
        const intervalId = setInterval(checkSession, 1000);

        // Listen for storage events from other tabs
        const handleStorage = (e) => {
            if (e.key === 'logout' || (e.key === 'token' && !e.newValue)) {
                invalidateSession();
            }
        };

        window.addEventListener('storage', handleStorage);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', handleStorage);
        };
    }, [isSessionActive]);

    // Block all renders when session is inactive
    if (!isSessionActive) {
        return null;
    }

    return (
        <SessionContext.Provider value={{ isSessionActive, invalidateSession, sessionId }}>
            {children}
        </SessionContext.Provider>
    );
};

SessionProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default SessionContext;
